import React from 'react';
import { generateItemKey } from '@/utils/generateItemKey';
import classNames from 'classnames';

type StepSelectorProps<T> = {
  items: T[];
  selectedItem: T;
  onItemSelect: (item: T) => void;
  getItemLabel: (item: T) => string;
  theme?: 'benefits' | 'howItWorks';
  className?: string;
};

function getTransform(indicatorPosition: string) {
  if (indicatorPosition === '0%') {
    return 'translateX(0%)';
  }
  return `translateX(-${indicatorPosition})`;
}

const themeColors = {
  benefits: {
    containerBg: 'bg-[#f3cbbc]',
    indicatorBg: 'bg-linear-to-t from-[#D64718] to-[#E98C6C]',
    selectedLabelColor: 'text-[#D64718]',
    unselectedLabelColor: 'text-black hover:text-[#D64718]',
  },
  howItWorks: {
    containerBg: 'bg-[#C3B5FF80]',
    indicatorBg: 'bg-linear-to-t from-[#C3B5FF] to-[#8875D5]',
    selectedLabelColor: 'text-[#8875D5]',
    unselectedLabelColor: 'text-black hover:text-[#8875D5]',
  },
};

const StepSelector = <T,>({
  className = '',
  getItemLabel,
  items,
  onItemSelect,
  selectedItem,
  theme = 'benefits',
}: StepSelectorProps<T>) => {
  const selectedIndex = items.findIndex((item, index) => {
    const itemKey = generateItemKey(item, index);
    const selectedKey = generateItemKey(
      selectedItem,
      items.indexOf(selectedItem)
    );
    return itemKey === selectedKey;
  });
  // Ensure selectedIndex is never -1 to keep indicator visible
  const safeSelectedIndex = selectedIndex === -1 ? 0 : selectedIndex;
  const indicatorPosition = `${(safeSelectedIndex / (items.length - 1)) * 100}%`;
  const transform = getTransform(indicatorPosition);

  const colors = themeColors[theme];

  return (
    <div
      className={classNames(
        'relative flex h-[16px] w-full max-w-[1000px] items-center justify-center overflow-y-visible rounded-[10px]',
        colors.containerBg,
        className
      )}
    >
      <div
        className={classNames(
          'absolute top-0 z-20 h-full w-[150px] rounded-[10px] shadow-md transition-all duration-500 ease-in-out max-sm:w-[80px]',
          colors.indicatorBg
        )}
        style={{ left: indicatorPosition, transform }}
      >
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-[8px] w-[8px] rounded-full bg-white" />
        </div>
      </div>

      <div className="relative z-10 flex h-[16px] w-full justify-between overflow-y-visible">
        {items.map((item, index) => {
          const itemKey = generateItemKey(item, index);
          const selectedKey = generateItemKey(
            selectedItem,
            items.indexOf(selectedItem)
          );
          const isSelected = itemKey === selectedKey;
          return (
            <div
              key={itemKey}
              className={classNames(
                'box-border flex w-full max-w-[150px] cursor-pointer flex-col items-center transition-all duration-300 ease-in-out hover:scale-105 max-sm:max-w-[80px]',
                isSelected ? 'font-medium' : 'font-normal'
              )}
              onClick={() => onItemSelect(item)}
            >
              <div className="flex h-[16px] items-center justify-center py-1">
                <div className="h-[6px] w-[6px] rounded-full bg-white" />
              </div>

              <div
                className={classNames(
                  "mt-3 text-center font-['Work_Sans'] text-[16px] leading-[20px] tracking-[0%] transition-all duration-300 ease-in-out max-sm:mt-2 max-sm:text-[12px] max-sm:leading-[14px]",
                  isSelected
                    ? colors.selectedLabelColor
                    : colors.unselectedLabelColor
                )}
              >
                {getItemLabel(item)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepSelector;
